import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { API_URL } from "../config";

interface BookingCar {
  id: number;
  booking_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  address: string;
  car_id: number;
  car_name?: string;
  car?: {
    id: number;
    name: string;
    model: string;
    color: string;
    number: string;
    type: string;
    engine_capacity: string;
    price_per_day: string;
    photos?: { path: string }[];
  };
  new_price: string;
  duration: number;
  status: string;
  notes?: string;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  bgPage: "#f8fafc",
  bgSubtle: "#fafafa",
  // Status colors - plus élégantes
  pending: "#f59e0b",
  pendingBg: "#fef3c7",
  confirmed: "#10b981",
  confirmedBg: "#d1fae5",
  completed: "#3b82f6",
  completedBg: "#dbeafe",
  cancelled: "#ef4444",
  cancelledBg: "#fee2e2",
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: colors.bgPage, padding: "20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
                <div style={{ width: 120, height: 24, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 6, animation: "shimmer 1.5s infinite" }}></div>
                <div style={{ width: 80, height: 32, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 8, animation: "shimmer 1.5s infinite" }}></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
                {[...Array(35)].map((_, i) => (
                  <div key={i} style={{ height: 80, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 8, animation: "shimmer 1.5s infinite" }}></div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "24px" }}>
              <div style={{ width: 140, height: 20, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 4, marginBottom: 16, animation: "shimmer 1.5s infinite" }}></div>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < 3 ? "1px solid #f1f5f9" : "none" }}>
                  <div style={{ width: 48, height: 48, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 8, animation: "shimmer 1.5s infinite" }}></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 100, height: 14, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 4, marginBottom: 4, animation: "shimmer 1.5s infinite" }}></div>
                    <div style={{ width: 80, height: 12, background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", borderRadius: 4, animation: "shimmer 1.5s infinite" }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CalendarBookingCars = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingCar[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedBookings, setSelectedBookings] = useState<BookingCar[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [todayBookings, setTodayBookings] = useState<BookingCar[]>([]);

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const bookingsRes = await axios.get(`${API_URL}/booking-cars`, config_token);
      const bookingsData = bookingsRes.data.data || [];
      
      const enrichedBookings = bookingsData.map((booking: BookingCar) => ({
        ...booking,
        car_name: booking.car?.name || `Car #${booking.car_id}`
      }));
      
      setBookings(enrichedBookings);
      
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const todayBookingsFiltered = enrichedBookings.filter((booking: BookingCar) => {
        const startDate = booking.booking_date?.split('T')[0] || booking.booking_date;
        const endDate = booking.end_date?.split('T')[0] || booking.end_date;
        return todayStr >= startDate && todayStr <= endDate;
      });
      setTodayBookings(todayBookingsFiltered);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error loading calendar data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Décalage pour commencer la semaine en Lundi
    const firstDayOfMonth = (new Date(year, month, 1).getDay() + 6) % 7;
    return { daysInMonth, firstDayOfMonth };
  };

  const isDateInBookingRange = (dateStr: string, booking: BookingCar) => {
    const startDate = booking.booking_date?.split('T')[0] || booking.booking_date;
    const endDate = booking.end_date?.split('T')[0] || booking.end_date;
    return dateStr >= startDate && dateStr <= endDate;
  };

  const getBookingsForDay = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(booking => isDateInBookingRange(dateStr, booking));
  };

  const handleDayClick = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, day);
    setSelectedDate(date);
    const dayBookings = getBookingsForDay(day);
    setSelectedBookings(dayBookings);
    setShowModal(true);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return colors.confirmed;
      case 'pending': return colors.pending;
      case 'cancelled': return colors.cancelled;
      case 'completed': return colors.completed;
      default: return colors.textMuted;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return colors.confirmedBg;
      case 'pending': return colors.pendingBg;
      case 'cancelled': return colors.cancelledBg;
      case 'completed': return colors.completedBg;
      default: return colors.bgSubtle;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'Confirmed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      case 'completed': return 'Completed';
      default: return status || 'Unknown';
    }
  };

  const getStatusDot = (status: string) => {
    const color = getStatusColor(status);
    return (
      <span
        style={{
          display: "inline-block",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: color,
          marginRight: "4px",
        }}
      />
    );
  };

  const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentDate);
  const today = new Date();
  const isToday = (day: number) => {
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Aujourd'hui en format court
  const todayStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "24px 20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 style={{ fontSize: "22px", fontWeight: 700, color: colors.primary, margin: 0 }}>
                <i className="fa-regular fa-calendar" style={{ marginRight: "10px", color: "#d3be76" }}></i>
                Rental Calendar
              </h1>
              <p style={{ color: colors.textMuted, fontSize: "13px", margin: "2px 0 0" }}>
                {bookings.length} total bookings · {todayBookings.length} today
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => navigate("/admin/list-rent-car")}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#475569",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.15s",
                }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#fff"; }}
              >
                <i className="fa-solid fa-list"></i> List
              </button>
              <button
                onClick={fetchData}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: colors.primary,
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.15s",
                }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
              >
                <i className="fa-solid fa-rotate"></i> Refresh
              </button>
            </div>
          </div>

          <div className="row g-4">
            {/* CALENDRIER */}
            <div className="col-12 col-lg-8">
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                {/* Calendar Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <button
                      onClick={goToPreviousMonth}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#fff"}
                    >
                      <i className="fa-solid fa-chevron-left" style={{ fontSize: "12px", color: colors.textMuted }}></i>
                    </button>
                    <h2 style={{ fontSize: "18px", fontWeight: 700, margin: 0, color: colors.text, minWidth: "150px", textAlign: "center" }}>
                      {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                    <button
                      onClick={goToNextMonth}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#fff"}
                    >
                      <i className="fa-solid fa-chevron-right" style={{ fontSize: "12px", color: colors.textMuted }}></i>
                    </button>
                  </div>
                  <button
                    onClick={goToToday}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      color: colors.primary,
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#fff"}
                  >
                    Today
                  </button>
                </div>

                {/* Day Headers - Lundi à Dimanche */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "4px",
                    marginBottom: "6px",
                  }}
                >
                  {DAYS.map((day) => (
                    <div
                      key={day}
                      style={{
                        padding: "6px",
                        textAlign: "center",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: colors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "4px",
                  }}
                >
                  {Array.from({ length: firstDayOfMonth }, (_, i) => (
                    <div key={`empty-${i}`} style={{ padding: "4px", minHeight: "80px" }} />
                  ))}

                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const dayBookings = getBookingsForDay(day);
                    const isTodayDate = isToday(day);
                    const isPast = new Date(currentDate.getFullYear(), currentDate.getMonth(), day) < new Date(new Date().setHours(0, 0, 0, 0));

                    return (
                      <div
                        key={day}
                        onClick={() => handleDayClick(day)}
                        style={{
                          padding: "4px",
                          minHeight: "80px",
                          borderRadius: "10px",
                          backgroundColor: isTodayDate ? "#f0f4f8" : "transparent",
                          border: isTodayDate ? `2px solid ${colors.primary}` : "1px solid transparent",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          position: "relative",
                        }}
                        onMouseOver={(e) => {
                          if (!isTodayDate) e.currentTarget.style.backgroundColor = "#fafafa";
                          e.currentTarget.style.borderColor = isTodayDate ? colors.primary : "#e2e8f0";
                        }}
                        onMouseOut={(e) => {
                          if (!isTodayDate) e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.borderColor = isTodayDate ? colors.primary : "transparent";
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: isTodayDate ? 700 : 500,
                            color: isPast ? colors.textFaint : colors.text,
                            marginBottom: "4px",
                            padding: "2px 4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {day}
                          {isTodayDate && (
                            <span
                              style={{
                                fontSize: "7px",
                                fontWeight: 700,
                                color: "#fff",
                                backgroundColor: colors.primary,
                                padding: "1px 5px",
                                borderRadius: "3px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              TODAY
                            </span>
                          )}
                        </div>

                        {/* Booking indicators - affiche les noms */}
                        {dayBookings.slice(0, 2).map((booking) => (
                          <div
                            key={booking.id}
                            style={{
                              fontSize: "9px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              backgroundColor: getStatusBg(booking.status),
                              color: getStatusColor(booking.status),
                              fontWeight: 500,
                              marginBottom: "2px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                            title={`${booking.firstname} ${booking.lastname} - ${booking.car_name}`}
                          >
                            {getStatusDot(booking.status)}
                            {booking.firstname} {booking.lastname}
                          </div>
                        ))}
                        {dayBookings.length > 2 && (
                          <div
                            style={{
                              fontSize: "9px",
                              color: colors.textFaint,
                              fontWeight: 500,
                              padding: "2px 4px",
                            }}
                          >
                            +{dayBookings.length - 2} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    marginTop: "16px",
                    paddingTop: "16px",
                    borderTop: "1px solid #f1f5f9",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "11px", fontWeight: 600, color: colors.textMuted }}>Status:</span>
                  {[
                    { label: "Pending", color: colors.pending, bg: colors.pendingBg },
                    { label: "Confirmed", color: colors.confirmed, bg: colors.confirmedBg },
                    { label: "Completed", color: colors.completed, bg: colors.completedBg },
                    { label: "Cancelled", color: colors.cancelled, bg: colors.cancelledBg },
                  ].map((item) => (
                    <span
                      key={item.label}
                      style={{
                        fontSize: "10px",
                        fontWeight: 600,
                        padding: "2px 10px",
                        borderRadius: "12px",
                        backgroundColor: item.bg,
                        color: item.color,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: item.color }}></span>
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* SIDEBAR - TODAY'S RENTALS */}
            <div className="col-12 col-lg-4">
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                  <div>
                    <h3 style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, margin: 0 }}>
                      <i className="fa-regular fa-calendar-check" style={{ marginRight: "8px", color: "#d3be76" }}></i>
                      Today's Rentals
                    </h3>
                    <p style={{ fontSize: "12px", color: colors.textMuted, margin: "2px 0 0" }}>
                      {todayStr}
                    </p>
                  </div>
                  <span
                    style={{
                      backgroundColor: colors.primary,
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                      padding: "2px 10px",
                      borderRadius: "12px",
                    }}
                  >
                    {todayBookings.length}
                  </span>
                </div>

                {todayBookings.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 12px", color: colors.textFaint }}>
                    <i className="fa-regular fa-calendar" style={{ fontSize: "32px", display: "block", marginBottom: "8px", color: "#e2e8f0" }}></i>
                    <p style={{ fontSize: "14px", margin: 0, fontWeight: 500 }}>No rentals today</p>
                    <p style={{ fontSize: "12px", margin: "4px 0 0" }}>All cars are available</p>
                  </div>
                ) : (
                  <>
                    {todayBookings.slice(0, 4).map((booking, index) => (
                      <div
                        key={booking.id}
                        style={{
                          display: "flex",
                          gap: "12px",
                          padding: "12px 8px",
                          borderBottom: index < todayBookings.length - 1 ? "1px solid #f1f5f9" : "none",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          borderRadius: "8px",
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#fafafa"; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        onClick={() => navigate(`/admin/details-rent-car/${booking.id}`)}
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            flexShrink: 0,
                            backgroundColor: "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.textFaint,
                          }}
                        >
                          {booking.car?.photos?.[0] ? (
                            <img
                              src={`${API_URL}/${booking.car.photos[0].path}`}
                              alt={booking.car_name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <i className="fa-solid fa-car" style={{ fontSize: "20px" }}></i>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: colors.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {booking.car_name}
                          </div>
                          <div style={{ fontSize: "12px", color: colors.textMuted }}>
                            {booking.firstname} {booking.lastname}
                          </div>
                          <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "2px" }}>
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: 600,
                                padding: "1px 8px",
                                borderRadius: "10px",
                                backgroundColor: getStatusBg(booking.status),
                                color: getStatusColor(booking.status),
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {getStatusDot(booking.status)}
                              {getStatusLabel(booking.status)}
                            </span>
                            <span style={{ fontSize: "10px", color: colors.textFaint }}>
                              {booking.duration}d
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {todayBookings.length > 4 && (
                      <button
                        onClick={() => {
                          setSelectedDate(new Date());
                          setSelectedBookings(todayBookings);
                          setShowModal(true);
                        }}
                        style={{
                          marginTop: "12px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "10px",
                          border: "1px dashed #e2e8f0",
                          background: "transparent",
                          color: colors.primary,
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#f8fafc"; e.currentTarget.style.borderColor = colors.primary; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
                      >
                        View all {todayBookings.length} bookings →
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Day Details */}
      {showModal && selectedDate && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "80vh",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #edf2f7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: 700, color: colors.primary, margin: 0 }}>
                  {MONTHS[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
                </h2>
                <p style={{ fontSize: "13px", color: colors.textMuted, margin: "2px 0 0" }}>
                  {selectedBookings.length} booking{selectedBookings.length > 1 ? "s" : ""}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  color: colors.textFaint,
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div style={{ padding: "16px 24px", overflowY: "auto", maxHeight: "400px" }}>
              {selectedBookings.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 16px", color: colors.textFaint }}>
                  <i className="fa-regular fa-calendar" style={{ fontSize: "32px", display: "block", marginBottom: "8px" }}></i>
                  No bookings on this day
                </div>
              ) : (
                selectedBookings.map((booking) => (
                  <div
                    key={booking.id}
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      marginBottom: "8px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                    onClick={() => {
                      setShowModal(false);
                      navigate(`/admin/details-rent-car/${booking.id}`);
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                          {getStatusDot(booking.status)}
                          {booking.firstname} {booking.lastname}
                        </div>
                        <div style={{ fontSize: "12px", color: colors.textMuted, marginTop: "2px" }}>
                          {booking.car_name || `Car #${booking.car_id}`}
                        </div>
                        <div style={{ fontSize: "11px", color: colors.textFaint, marginTop: "2px" }}>
                          <i className="fa-regular fa-clock" style={{ marginRight: "4px" }}></i>
                          {formatDate(booking.booking_date)} → {formatDate(booking.end_date)} · {booking.duration} days
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: colors.primary }}>
                          ${booking.new_price}
                        </div>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            backgroundColor: getStatusBg(booking.status),
                            color: getStatusColor(booking.status),
                          }}
                        >
                          {getStatusLabel(booking.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div
              style={{
                padding: "14px 24px",
                borderTop: "1px solid #edf2f7",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarBookingCars;